import {
  // lazy,
  Suspense,
  useCallback,
} from 'react';
import { createBrowserHistory } from 'history';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router';
import { useReactiveVar } from '@apollo/client';
import styled from '@emotion/styled';
import WrappedPage from '../Layout/WrappedPage';
import LiftReportPage from '../connected/LiftReportPage';
import LiftReportLayout from '../Layout/LiftReportLayout';
import CustomReportLayout from '../Layout/CustomReportLayout';
import CustomReportPage from '../connected/CustomReportPage';
import IframePage from '../connected/IframePage';
import IframeLayout from '../Layout/IframeLayout';
import SimpleLayout from '../Layout/SimpleLayout';
// import NoSideBarLayout from '../Layout/NoSideBarLayout';
import PreviewCommentPage from '../connected/PreviewCommentPage';
import PreviewCommentEditPage from '../connected/PreviewCommentEditPage';
import BasicTopPage from '../page/BasicTopPage';
import SitedockPage from '../page/SitedockPage';
import SitedockListPage from '../page/SitedockListPage';
import SegmentPage from '../page/SegmentPage';
import SiteSearchAnalyticsPage from '../page/SiteSearchAnalyticsPage';
import LinkPrefetchPage from '../connected/LinkPrefetchPage';
import CommentBoardLayout from '../Layout/CommentBoardLayout';

import useMessageListener from '../../hooks/useMessageListener';
import { lastAccessedOrganizationVar } from '../../cache';
import GoalSettingDetailPage from '../page/GoalSettingDetailPage';
import GoalSettingPage from '../page/GoalSettingPage';
import JavascriptCodePage from '../connected/JavaScriptCodePage';
import VariationDetailsPage from '../page/VariationDetailsPage';
import ExperimentDetailsPage from '../page/ExperimentDetailsPage';
import ExperimentsListPage from '../page/ExperimentsListPage';
import SimpleLayoutDraft from '../Layout/SimpleLayoutDraft';

// const UserPage = lazy(() => import('../connected/UserPage'));
// const ProductPage = lazy(() => import('../connected/ProductPage'));

type Props = {
  className?: string;
};

// For react-router-dom > 6.3.0 starts emitting eroors
// TODO: resolve the errors and ver-up the package
const SignedInScreen = ({ className }: Props) => {
  const { pathname, search } = useLocation();
  const org = useReactiveVar(lastAccessedOrganizationVar);
  const navigate = useNavigate();

  const handlePostMessage = useCallback(
    (event) => {
      const getOrganizationFromUrlPath = (pathString: string): string => {
        let paths = pathString.split('/');
        for (let i = 0; i < paths.length; i++) {
          if (paths[i] === 'organizations' || paths[i] === 'users') {
            return i + 1 < paths.length ? paths[i + 1] : '';
          }
        }
        return '';
      };

      if (typeof event.data !== 'string') {
        return;
      }
      if (window.location.pathname.includes('/growth-hacker')) return;

      const [pathStr, queryStr] = event.data.split('?');
      const orgIdFromPostmessage = getOrganizationFromUrlPath(pathStr) ?? '';
      const windowQuery = new URLSearchParams(window.location.search);
      const eventQuery = new URLSearchParams(queryStr);
      windowQuery.delete('header'), eventQuery.delete('header'); //remove query "header=none"
      windowQuery.delete('org'), eventQuery.delete('org');
      windowQuery.sort(), eventQuery.sort();
      const wParam = windowQuery.toString();
      const eParam = eventQuery.toString();
      if (orgIdFromPostmessage !== '' && org?.id !== orgIdFromPostmessage)
        return navigate(`/engine/experiments?org=${org}`);

      if (wParam !== eParam && eParam !== '') {
        // TODO change here if there is a way to update the url without reloading the iframe
        // because this history is diverging from the main history instance
        const history = createBrowserHistory();
        history.replace(`/engine${pathStr}?${eParam}`);
      }
    },
    [pathname, search, navigate, org],
  );

  useMessageListener(handlePostMessage);

  if (!org)
    return (
      // independent User beloging to no organizations
      <Root className={className}>
        <Suspense fallback={<div />}>
          <Routes>
            <Route path="/:url*(/+)" element={<Navigate replace to={pathname.slice(0, -1) + search} />} />
            <Route caseSensitive={true} path="basic">
              <Route index={true} element={<WrappedPage page={BasicTopPage} layout={SimpleLayout} />} />
            </Route>
            <Route caseSensitive={true} path="engine">
              <Route
                caseSensitive={true}
                path="my_experiments/*"
                element={<WrappedPage page={IframePage} layout={IframeLayout} />}
              />
              <Route
                caseSensitive={true}
                path="experiments/*"
                element={<WrappedPage page={IframePage} layout={IframeLayout} />}
              />
              <Route
                caseSensitive={true}
                path="comment-board"
                element={<WrappedPage page={PreviewCommentPage} layout={SimpleLayout} />}
              />
              <Route
                caseSensitive={true}
                path="comment-board/:id"
                element={<WrappedPage page={PreviewCommentEditPage} layout={CommentBoardLayout} />}
              />
              <Route
                caseSensitive={true}
                path="comment-board/:id/:page"
                element={<WrappedPage page={PreviewCommentEditPage} layout={CommentBoardLayout} />}
              />
              <Route path="*" element={<Navigate to={`/my_experiments${search}`} />} />
            </Route>
            <Route caseSensitive={true} path="growth-hacker">
              <Route index={true} element={<Navigate replace to={`/growth-hacker/me${search}`} />} />
              <Route caseSensitive={true} path="me" element={<WrappedPage page={IframePage} layout={IframeLayout} />} />
              <Route
                caseSensitive={true}
                path=":profileId"
                element={<WrappedPage page={IframePage} layout={IframeLayout} />}
              />
              <Route path="*" element={<Navigate replace to={`/growth-hacker/me${search}`} />} />
            </Route>
            <Route path="*" element={<Navigate replace to={`/growth-hacker/me${search}`} />} />
          </Routes>
        </Suspense>
      </Root>
    );

  return (
    <Root className={className}>
      <Suspense fallback={<div />}>
        <Routes>
          <Route path="/:url*(/+)" element={<Navigate replace to={pathname.slice(0, -1) + search} />} />
          <Route caseSensitive={true} path="engine">
            <Route index element={<Navigate replace to={`/engine/experiments${search}`} />} />
            <Route
              caseSensitive={true}
              path="liftreport"
              element={<WrappedPage page={LiftReportPage} layout={LiftReportLayout} />}
            />
            <Route
              caseSensitive={true}
              path="customreport"
              element={<WrappedPage page={CustomReportPage} layout={CustomReportLayout} />}
            />
            <Route caseSensitive={true} path="sitedock-v2">
              <Route index element={<WrappedPage page={SitedockListPage} layout={SimpleLayout} />} />
              <Route
                caseSensitive={true}
                path=":project_id"
                element={<WrappedPage page={SitedockPage} layout={SimpleLayout} />}
              />
            </Route>
            <Route
              caseSensitive={true}
              path="segment"
              element={<WrappedPage page={SegmentPage} layout={SimpleLayout} />}
            />
            <Route
              caseSensitive={true}
              path="site-analytics/*"
              element={<WrappedPage page={IframePage} layout={IframeLayout} />}
            />
            <Route
              caseSensitive={true}
              path="exp-portfolio/*"
              element={<WrappedPage page={IframePage} layout={IframeLayout} />}
            />
            <Route
              caseSensitive={true}
              path="sitedock/*"
              element={<WrappedPage page={IframePage} layout={IframeLayout} />}
            />
            <Route
              caseSensitive={true}
              path="my_experiments/*"
              element={<WrappedPage page={IframePage} layout={IframeLayout} />}
            />
            <Route caseSensitive={true} path="experiments">
              <Route index element={<WrappedPage page={IframePage} layout={IframeLayout} />} />
              <Route
                caseSensitive={true}
                path=":experiment_id"
                element={<WrappedPage page={IframePage} layout={IframeLayout} />}
              />
            </Route>
            <Route caseSensitive={true} path="renewal_experiments">
              <Route index element={<WrappedPage page={ExperimentsListPage} layout={SimpleLayoutDraft} />} />
              <Route
                caseSensitive={true}
                path=":experiment_id"
                element={<WrappedPage page={ExperimentDetailsPage} layout={SimpleLayout} />}
              />
              <Route
                caseSensitive={true}
                path=":experiment_id/variations/:variation_id"
                element={<WrappedPage page={VariationDetailsPage} layout={SimpleLayout} />}
              />
            </Route>
            <Route
              caseSensitive={true}
              path="users/*"
              element={<WrappedPage page={IframePage} layout={IframeLayout} />}
            />
            <Route
              caseSensitive={true}
              path="experiments_b"
              element={<WrappedPage page={ExperimentsListPage} layout={SimpleLayoutDraft} />}
            />
            <Route
              caseSensitive={true}
              path="experiments_b/:experimentsid"
              element={<WrappedPage page={ExperimentDetailsPage} layout={SimpleLayout} />}
            />
            <Route
              caseSensitive={true}
              path="goals/*"
              element={<WrappedPage page={IframePage} layout={IframeLayout} />}
            />
            <Route
              caseSensitive={true}
              path="organizations/*"
              element={<WrappedPage page={IframePage} layout={IframeLayout} />}
            />
            <Route
              caseSensitive={true}
              path="settings/*"
              element={<WrappedPage page={IframePage} layout={IframeLayout} />}
            />
            <Route
              caseSensitive={true}
              path="comment-board"
              element={<WrappedPage page={PreviewCommentPage} layout={SimpleLayout} />}
            />
            {/* TODO: パスの修正 */}
            <Route
              caseSensitive={true}
              path="goal-setting"
              element={<WrappedPage page={GoalSettingPage} layout={SimpleLayout} />}
            />
            <Route
              caseSensitive={true}
              path="goal-setting/:id"
              element={<WrappedPage page={GoalSettingDetailPage} layout={SimpleLayout} />}
            />
            <Route
              caseSensitive={true}
              path="js_code"
              element={<WrappedPage page={JavascriptCodePage} layout={SimpleLayout} />}
            />
            <Route
              caseSensitive={true}
              path="comment-board/:id"
              element={<WrappedPage page={PreviewCommentEditPage} layout={CommentBoardLayout} />}
            />
            <Route
              caseSensitive={true}
              path="comment-board/:id/:page"
              element={<WrappedPage page={PreviewCommentEditPage} layout={CommentBoardLayout} />}
            />
            <Route
              caseSensitive={true}
              path="site-search-analytics"
              element={<WrappedPage page={SiteSearchAnalyticsPage} layout={SimpleLayout} />}
            />
            <Route
              caseSensitive={true}
              path="link-prefetch"
              element={<WrappedPage page={LinkPrefetchPage} layout={SimpleLayout} />}
            />
          </Route>
          {/* <Route caseSensitive={true} path="/business" element={<WrappedPage page={BusinessPage} layout={MainLayout} />} />
          <Route caseSensitive={true} path="/users" element={<WrappedPage page={UserPage} layout={MainLayout}  />} />
          <Route caseSensitive={true} path="/products" element={<WrappedPage page={ProductPage} layout={MainLayout}  />} /> */}
          <Route caseSensitive={true} path="growth-hacker">
            <Route index={true} element={<Navigate replace to={`/growth-hacker/me${search}`} />} />
            <Route caseSensitive={true} path="me" element={<WrappedPage page={IframePage} layout={IframeLayout} />} />
            <Route
              caseSensitive={true}
              path=":profileId"
              element={<WrappedPage page={IframePage} layout={IframeLayout} />}
            />
            <Route path="*" element={<Navigate replace to={`/growth-hacker/me${search}`} />} />
          </Route>
          <Route path="*" element={<Navigate to={`/engine/experiments${search}`} />} />
        </Routes>
      </Suspense>
    </Root>
  );
};

const Root = styled('div')({});

export default SignedInScreen;
